import './style.css';
import * as THREE from 'three';
import { SpaceshipRoom } from './scene/SpaceshipRoom';
import { ScreenManager } from './scene/ScreenManager';
import { CameraControls } from './controls/CameraControls';
import { WarpEffect } from './effects/WarpEffect';
import { languageManager } from './i18n/LanguageManager';
import { ForceTrainerGame } from './game/ForceTrainerGame';

// Interface for any game mode
interface GameMode {
    dispose(): void;
}

class SpaceshipExperience implements GameMode {
    private scene!: THREE.Scene;
    private camera!: THREE.PerspectiveCamera;
    private renderer!: THREE.WebGLRenderer;
    private canvas!: HTMLCanvasElement;

    private room!: SpaceshipRoom;
    private screenManager!: ScreenManager;
    private cameraControls!: CameraControls;

    private clock!: THREE.Clock;
    private isRunning = true;
    private isInitialized = false;
    private animationId: number = 0;

    constructor() {
        // Show entry screen first
        this.showEntryScreen();

        // Create language switcher
        this.createLanguageSwitcher();
    }

    private createLanguageSwitcher(): void {
        const languages = [
            { code: 'en', label: '🇺🇸', name: 'English' },
            { code: 'zh-HK', label: '🇭🇰', name: '繁體中文' },
            { code: 'zh-CN', label: '🇨🇳', name: '简体中文' },
            { code: 'ja', label: '🇯🇵', name: '日本語' },
            { code: 'ko', label: '🇰🇷', name: '한국어' },
            { code: 'es', label: '🇪🇸', name: 'Español' },
        ];

        const container = document.createElement('div');
        container.id = 'lang-switcher';
        container.innerHTML = `
            <div class="lang-current" id="lang-current-btn">
                <span>🇺🇸</span>
            </div>
            <div class="lang-dropdown" id="lang-dropdown">
                ${languages.map(lang => `
                    <button class="lang-option" data-lang="${lang.code}">
                        <span class="lang-flag">${lang.label}</span>
                        ${lang.name}
                    </button>
                `).join('')}
            </div>
        `;

        // Find the spacer and replace it or append to header
        const navHeader = document.getElementById('nav-header');
        const navSpacer = document.getElementById('nav-spacer');
        
        if (navSpacer && navHeader) {
            navHeader.replaceChild(container, navSpacer);
        } else {
            // Fallback
            document.body.appendChild(container);
        }

        const currentBtn = container.querySelector('#lang-current-btn') as HTMLElement;
        const dropdown = container.querySelector('#lang-dropdown') as HTMLElement;
        const options = container.querySelectorAll('.lang-option');

        // Toggle dropdown
        currentBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            dropdown.classList.toggle('open');
        });

        // Close on click outside
        document.addEventListener('click', () => {
            dropdown.classList.remove('open');
        });

        // Handle selection
        options.forEach(opt => {
            opt.addEventListener('click', () => {
                const langCode = opt.getAttribute('data-lang');
                if (langCode) {
                    languageManager.setLanguage(langCode as any);
                }
            });
        });

        // Update UI on change
        languageManager.subscribe((lang) => {
            const selected = languages.find(l => l.code === lang);
            if (selected) {
                const span = currentBtn.querySelector('span');
                if (span) span.textContent = selected.label;
            }

            // Highlight active option
            options.forEach(opt => {
                if (opt.getAttribute('data-lang') === lang) {
                    opt.classList.add('active');
                } else {
                    opt.classList.remove('active');
                }
            });
        });
    }

    private showEntryScreen(): void {
        // Create entry container
        const entryContainer = document.createElement('div');
        entryContainer.id = 'entry-screen';
        entryContainer.innerHTML = `
            <div class="entry-content">
                <div class="entry-logo">
                    <span class="logo-text">MERCURY</span>
                    <span class="logo-tagline">TECHNOLOGY SOLUTIONS</span>
                </div>
                <div class="entry-message">PREPARING YOUR EXPERIENCE</div>
                <button class="entry-button" id="enter-button">
                    <span class="button-text">ENTER THE STATION</span>
                    <span class="button-icon">▶</span>
                </button>
            </div>
            <div class="entry-stars"></div>
        `;
        document.body.appendChild(entryContainer);

        // Add entry screen styles
        this.addEntryStyles();

        // Initial localization
        this.updateEntryText();

        // Subscribe to changes (in case language is switched while on entry screen, 
        // though switcher might not be visible yet, good practice)
        languageManager.subscribe(() => this.updateEntryText());

        // Create ambient stars animation
        this.createEntryStars(entryContainer.querySelector('.entry-stars') as HTMLElement);

        // Handle enter button
        const enterButton = document.getElementById('enter-button');
        enterButton?.addEventListener('click', () => {
            enterButton.classList.add('clicked');
            this.startWarpSequence(entryContainer);
        });
    }

    private updateEntryText(): void {
        const ui = languageManager.getUI();
        const msg = document.querySelector('.entry-message');
        const btnText = document.querySelector('.button-text');

        if (msg) msg.textContent = ui.common.preparing;
        if (btnText) btnText.textContent = ui.common.enterStation;
    }

    private addEntryStyles(): void {
        const style = document.createElement('style');
        style.textContent = `
            #entry-screen {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(135deg, #000510 0%, #0a0a20 50%, #0f0520 100%);
                z-index: 1500;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
            }

            .entry-stars {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
            }

            .entry-star {
                position: absolute;
                width: 2px;
                height: 2px;
                background: white;
                border-radius: 50%;
                animation: twinkle 2s ease-in-out infinite;
            }

            @keyframes twinkle {
                0%, 100% { opacity: 0.3; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.5); }
            }

            .entry-content {
                text-align: center;
                z-index: 10;
                animation: fadeInUp 1s ease-out;
            }

            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(30px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            .entry-logo {
                margin-bottom: 40px;
            }

            .logo-text {
                display: block;
                font-family: 'Orbitron', sans-serif;
                font-size: 48px;
                font-weight: 700;
                letter-spacing: 12px;
                color: #ffffff;
                text-shadow: 
                    0 0 30px rgba(0, 212, 255, 0.5),
                    0 0 60px rgba(124, 58, 237, 0.3);
                animation: logoGlow 3s ease-in-out infinite;
            }

            @keyframes logoGlow {
                0%, 100% { 
                    text-shadow: 
                        0 0 30px rgba(0, 212, 255, 0.5),
                        0 0 60px rgba(124, 58, 237, 0.3);
                }
                50% { 
                    text-shadow: 
                        0 0 40px rgba(0, 212, 255, 0.8),
                        0 0 80px rgba(124, 58, 237, 0.5);
                }
            }

            .logo-tagline {
                display: block;
                font-family: 'Inter', sans-serif;
                font-size: 12px;
                font-weight: 300;
                letter-spacing: 6px;
                color: #6b7280;
                margin-top: 12px;
            }

            .entry-message {
                font-family: 'Orbitron', sans-serif;
                font-size: 11px;
                letter-spacing: 4px;
                color: #00d4ff;
                margin-bottom: 50px;
                opacity: 0.8;
                animation: pulse 2s ease-in-out infinite;
            }

            .entry-button {
                background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%);
                border: 1px solid rgba(0, 212, 255, 0.4);
                color: #ffffff;
                font-family: 'Orbitron', sans-serif;
                font-size: 14px;
                letter-spacing: 4px;
                padding: 18px 50px;
                cursor: pointer;
                position: relative;
                overflow: hidden;
                transition: all 0.3s ease;
                display: flex;
                align-items: center;
                gap: 15px;
            }

            .entry-button::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.2), transparent);
                transition: left 0.5s ease;
            }

            .entry-button:hover {
                border-color: rgba(0, 212, 255, 0.8);
                box-shadow: 
                0 0 30px rgba(0, 212, 255, 0.3),
                inset 0 0 20px rgba(0, 212, 255, 0.1);
                transform: scale(1.02);
            }

            .entry-button:hover::before {
                left: 100%;
            }

            .entry-button.clicked {
                pointer-events: none;
                animation: buttonPulse 0.5s ease-out;
            }

            @keyframes buttonPulse {
                0% { transform: scale(1); }
                50% { transform: scale(1.05); }
                100% { transform: scale(1); opacity: 0.5; }
            }

            .button-icon {
                font-size: 12px;
                color: #00d4ff;
                animation: arrowPulse 1.5s ease-in-out infinite;
            }

            @keyframes arrowPulse {
                0%, 100% { transform: translateX(0); }
                50% { transform: translateX(5px); }
            }

            #entry-screen.fading {
                pointer-events: none;
            }
        `;
        document.head.appendChild(style);
    }

    private createEntryStars(container: HTMLElement): void {
        for (let i = 0; i < 100; i++) {
            const star = document.createElement('div');
            star.className = 'entry-star';
            star.style.left = `${Math.random() * 100}%`;
            star.style.top = `${Math.random() * 100}%`;
            star.style.animationDelay = `${Math.random() * 2}s`;
            star.style.animationDuration = `${1.5 + Math.random() * 2}s`;

            // Random colors
            const colors = ['#ffffff', '#00d4ff', '#7c3aed'];
            star.style.background = colors[Math.floor(Math.random() * colors.length)];
            star.style.boxShadow = `0 0 ${2 + Math.random() * 4}px ${star.style.background}`;

            container.appendChild(star);
        }
    }

    private startWarpSequence(entryScreen: HTMLElement): void {
        // Initialize warp effect
        const warp = new WarpEffect(document.body);

        // Hide entry content
        const content = entryScreen.querySelector('.entry-content') as HTMLElement;
        content.style.transition = 'opacity 0.5s ease-out, transform 0.5s ease-out';
        content.style.opacity = '0';
        content.style.transform = 'scale(0.9)';

        setTimeout(() => {
            entryScreen.classList.add('fading');
            entryScreen.style.background = 'transparent';

            // Start warp
            warp.startWarp(() => {
                // Remove entry screen
                entryScreen.remove();

                // Initialize main experience
                this.initializeExperience();
            });
        }, 500);
    }

    private initializeExperience(): void {
        console.log('SpaceshipExperience: Initializing...');

        // Get canvas
        this.canvas = document.getElementById('three-canvas') as HTMLCanvasElement;
        if (!this.canvas) {
            throw new Error('Canvas element not found!');
        }

        // Initialize Three.js
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x0a0a15);
        this.scene.fog = new THREE.Fog(0x0a0a15, 20, 40);

        // Camera
        this.camera = new THREE.PerspectiveCamera(
            60,
            window.innerWidth / window.innerHeight,
            0.1,
            100
        );
        this.camera.position.set(0, 1.6, 0);

        // Renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            alpha: false,
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.5;

        // Clock
        this.clock = new THREE.Clock();

        // Create scene elements
        this.room = new SpaceshipRoom();
        this.scene.add(this.room.group);

        // Screen manager
        this.screenManager = new ScreenManager(this.scene, this.camera, this.canvas);

        // Camera controls
        this.cameraControls = new CameraControls(this.camera, this.canvas);
        this.canvas.style.cursor = 'grab';

        // Event listeners
        window.addEventListener('resize', this.onResize.bind(this));

        // Hide loading screen (the original one in HTML)
        this.hideLoadingScreen();

        // Start animation loop
        this.isInitialized = true;
        this.animate();

        console.log('SpaceshipExperience: Initialization complete!');
    }

    private hideLoadingScreen(): void {
        const loadingScreen = document.getElementById('loading-screen');
        if (loadingScreen) {
            loadingScreen.classList.add('hidden');
        }
    }

    private onResize(): void {
        if (!this.isInitialized) return;

        const width = window.innerWidth;
        const height = window.innerHeight;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }

    private animate(): void {
        if (!this.isRunning || !this.isInitialized) return;

        this.animationId = requestAnimationFrame(this.animate.bind(this));

        const deltaTime = this.clock.getDelta();
        const elapsedTime = this.clock.getElapsedTime();

        // Update components
        this.room.update(elapsedTime);
        this.screenManager.update(deltaTime);
        this.cameraControls.update(deltaTime);

        // Render
        this.renderer.render(this.scene, this.camera);
    }

    public dispose(): void {
        this.isRunning = false;
        cancelAnimationFrame(this.animationId);
        
        window.removeEventListener('resize', this.onResize.bind(this));

        if (this.isInitialized) {
            this.room.dispose();
            this.screenManager.dispose();
            this.cameraControls.dispose();
            this.renderer.dispose();
            
            // Clean up scene
            this.scene.clear();
        }
        
        // Restore cursor
        if (this.canvas) this.canvas.style.cursor = 'auto';
    }
}

// App Manager
class AppManager {
    public currentGame: GameMode | null = null;
    
    constructor() {
        this.setupMenu();
        // Start with Spaceship Experience
        this.switchToHome();
    }
    
    private setupMenu() {
        // Initialize hamburger menu
        const hamburgerBtn = document.getElementById('hamburger-btn');
        const hamburgerMenu = document.getElementById('hamburger-menu');

        if (hamburgerBtn && hamburgerMenu) {
            hamburgerBtn.addEventListener('click', () => {
                hamburgerBtn.classList.toggle('active');
                hamburgerMenu.classList.toggle('closed');
            });

            // Close menu when clicking outside
            document.addEventListener('click', (e) => {
                if (!hamburgerBtn.contains(e.target as Node) && !hamburgerMenu.contains(e.target as Node)) {
                    hamburgerBtn.classList.remove('active');
                    hamburgerMenu.classList.add('closed');
                }
            });

            // Menu Items
            document.getElementById('menu-home')?.addEventListener('click', (e) => {
                 e.preventDefault();
                 this.switchToHome();
                 hamburgerBtn.classList.remove('active');
                 hamburgerMenu.classList.add('closed');
            });

            document.getElementById('menu-force-trainer')?.addEventListener('click', (e) => {
                 e.preventDefault();
                 this.switchToForceTrainer();
                 hamburgerBtn.classList.remove('active');
                 hamburgerMenu.classList.add('closed');
            });
        }
    }
    
    private switchToHome() {
        if (this.currentGame instanceof SpaceshipExperience) return;
        
        if (this.currentGame) {
            this.currentGame.dispose();
        }
        
        // Reset specific UI elements if needed (Force Trainer might have added some)
        
        this.currentGame = new SpaceshipExperience();
    }
    
    private switchToForceTrainer() {
        if (this.currentGame instanceof ForceTrainerGame) return;
        
        if (this.currentGame) {
            this.currentGame.dispose();
        }
        
        const canvas = document.getElementById('three-canvas') as HTMLCanvasElement;
        if (canvas) {
             this.currentGame = new ForceTrainerGame(canvas);
        }
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    new AppManager();
});
